import { type FC, memo, type PropsWithChildren } from "react";
import Pagination from "../Pagination";
import { NoSearchResults } from "../NoSearchResults";
import { useDispatch, useSelector } from "react-redux";
import {
  selectContentPageAmount,
  selectContentResultAmount,
  selectContentResults,
  selectIsInFlight,
} from "../../../redux/searchSelectors";
import { type RootState } from "../../../../redux/store";
import { changeContentResultsPage } from "../../../redux/actions";
import NotFoundContactUsBar from "../../NotFoundContactUsBar/NotFoundContactUsBar";
import { Card } from "../../../../Configurators/Linear/components/Card";
import { type ContentDataItem } from "../../../../utils/models";
import { type BreadcrumbItem } from "../../../../jspComponents/Breadcrumbs/NavigationBreadcrumbs";
import { ArrowLink, Link, LinkType } from "../../../../common/components/Link";
import { useIsMinTabletSize } from "../../../../common/hooks/useMediaQuery";
import { Icon } from "../../../../common/components/icon/Icon";
import { IconType } from "../../../../common/components/icon/IconType";
import parseTruncate from "../../../../utils/parseTruncate";
import { useTranslation } from "react-i18next";

const ContentResults = () => {
  const resultAmount = useSelector(selectContentResultAmount);
  const currentPage = useSelector(selectCurrentContentPage);
  const pageAmount = useSelector(selectContentPageAmount);
  const results = useSelector(selectContentResults);

  const inFlight = useSelector(selectIsInFlight);

  const dispatch = useDispatch();
  const onPageChanged = (newPageNumber: number) => dispatch(changeContentResultsPage(newPageNumber));

  return (
    <>
      {!inFlight && (
        <>
          {resultAmount > 0 && (
            <>
              <ul className={"scha__srp__content-results__list"}>
                {results.map((item) => (
                  <li className={"scha__srp__content-results__list-item"}>
                    <ContentResultItem item={item} />
                  </li>
                ))}
              </ul>
              {pageAmount > 1 && (
                <div className="scha__srp__pagination">
                  <Pagination
                    pageAmount={pageAmount}
                    currentPage={currentPage}
                    onPageChange={onPageChanged}
                    renderHrefs={true}
                    goToIdAfterChange={"#resultsHeader"}
                  />
                </div>
              )}
            </>
          )}
          {resultAmount === 0 && <NoSearchResults />}
          <NotFoundContactUsBar className="scha__srp__not-found-disc" />
        </>
      )}
    </>
  );
};

const selectCurrentContentPage = (state: RootState) => state.srp.contentPage;

export default memo(ContentResults);

const ContentResultItem = memo(({ item }: { item: ContentDataItem }) => {
  const showBreadcrumbs = useIsMinTabletSize();
  const truncateTexts = !showBreadcrumbs;

  const titleParams = truncateTexts
    ? { children: parseTruncate(item.title, 30, "...") }
    : { dangerouslySetInnerHTML: { __html: item.title } };
  const contentParams = truncateTexts
    ? { children: parseTruncate(item.content, 130, "...") }
    : { dangerouslySetInnerHTML: { __html: item.content } };

  return (
    <Card className={"p-3"} disableHover={true}>
      <Link variant={LinkType.SECONDARY} href={item.label} className={"d-flex flex-direction-column flex-align-start"}>
        <p className={"title-large f-bold my-0"} {...titleParams} />
        <p className={"label-large mt-2 mb-0"} {...contentParams} />
      </Link>
      {showBreadcrumbs && item.breadcrumbs && <Breadcrumbs items={item.breadcrumbs} />}
    </Card>
  );
});

const Breadcrumbs = ({ items }: { items: BreadcrumbItem[] }) => {
  return (
    <ol className="scha__srp__content-results__list-item__breadcrumb">
      {items.map((item) => (
        <Breadcrumb item={item} />
      ))}
    </ol>
  );
};

const Breadcrumb: FC<{ item: BreadcrumbItem; isActive?: boolean }> = ({ item: { name, url } }) => {
  const { t } = useTranslation();
  // We should not try to translate the search query or designations that contain ":"
  name = name ?? "";
  const label = url.includes("search?text") ? name : name.includes(":") ? name : t(name);

  if (url) {
    return (
      <li>
        <ArrowLink href={url} variant={LinkType.NAVIGATION} className={"scha__link--content-bc"}>
          {label}
        </ArrowLink>
      </li>
    );
  }
  return (
    <li>
      <FakeArrowLink>{label}</FakeArrowLink>
    </li>
  );
};

const FakeArrowLink = ({ children }: PropsWithChildren) => {
  return (
    <div className={"scha__link disabled scha__link--content-bc"}>
      <span className="scha__link__icon scha__link__icon--before">
        <Icon type={IconType.ChevronRight} className={"scha__link--arrow-link"} />
      </span>
      {children}
    </div>
  );
};
